import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material'
import { AddBox } from '@mui/icons-material'
import SvgIconStyle from 'components/SvgIconStyle'
import { useRouter } from 'next/router'
import useCreateEventRedirect from 'hooks/commons/useCreateEventRedirect'
import { PATH_DASHBOARD } from 'routes/paths'
import useAuth from 'hooks/useAuth'
import { UserTypes } from '~/@types/profile'
import useLocales from 'hooks/useLocales'
import useSession from 'hooks/commons/useSession'

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 22, height: 22 }}
  />
)

const ICONS = {
  dashboard: getIcon('ic_dashboard'),
  components: getIcon('ic_components'),
  animate: getIcon('ic_animate'),
}

// ----------------------------------------------------------------------

export default function NavbarBottom() {
  const { t } = useLocales()
  const { pathname, push } = useRouter()
  const { user } = useAuth()
  const { canPerform } = useSession()
  const isCreator = user.role === UserTypes.Creator

  const createEventRedirect = useCreateEventRedirect()

  // Gated only for a Creator: the actions are Creator-Center-scoped, so gating
  // a promoter on them would empty their nav.
  const canViewCreatorEntry = (action: string) =>
    !isCreator || canPerform(action)

  // Which tab the current route belongs to. Longest-prefix first so
  // /dashboard/balance/withdraw still lights Saldo.
  const selectedFromRoute = (): string | false => {
    if (pathname.startsWith(PATH_DASHBOARD.general.balance.root)) {
      return 'saldo'
    }
    if (pathname.startsWith(PATH_DASHBOARD.creator_center.my_content)) {
      return 'produk'
    }
    if (pathname.startsWith(PATH_DASHBOARD.creator_center.dashboard)) {
      return 'dasbor'
    }
    return false
  }

  // Creator Center for every role: the promoter-program destinations are retired.
  const bottomNavHandler = (value: any) => {
    let path = ``
    if (value === 'dasbor') {
      path = PATH_DASHBOARD.creator_center.dashboard
    } else if (value === 'produk') {
      path = PATH_DASHBOARD.creator_center.my_content
    } else if (value === 'saldo') {
      path = PATH_DASHBOARD.general.balance.root
    }
    return `${path}?source=${
      pathname ? pathname : 'unknown'
    }&source_action=click-bottom-nav-${value}`
  }

  return (
    <Paper
      sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
      elevation={3}
    >
      <BottomNavigation
        showLabels
        // Derived only from the route, with no tapped-value state: the tap
        // navigates, and the new route decides what is lit. Holding state as
        // well froze the highlight on the last tap — Produk stayed lit after
        // going back to the dashboard.
        value={selectedFromRoute()}
        onChange={(_event, newValue) => {
          const isCreateContent = newValue === 'create-konten'
          if (isCreateContent) {
            createEventRedirect()
          } else {
            push(bottomNavHandler(newValue))
          }
        }}
      >
        {canViewCreatorEntry('dashboard:view') && (
          <BottomNavigationAction
            label="Dasbor"
            icon={ICONS.dashboard}
            value="dasbor"
          />
        )}
        {canViewCreatorEntry('product:view') && (
          <BottomNavigationAction
            label={isCreator ? 'Produk' : 'Cari Konten'}
            icon={ICONS.components}
            value="produk"
          />
        )}
        {isCreator && canPerform('product:create') && (
          <BottomNavigationAction
            label={t('key_create')}
            // Flat, the same weight as its neighbours — the raised 48px circle
            // is not in the design (node 715-38777).
            icon={<AddBox sx={{ width: 22, height: 22 }} />}
            value="create-konten"
          />
        )}
        {canPerform('balance:view') && (
          <BottomNavigationAction
            label="Saldo"
            icon={ICONS.animate}
            value="saldo"
          />
        )}
      </BottomNavigation>
    </Paper>
  )
}
